// Per-child dependency releases (Form 8332): which children can be released, the combinations the Exemption releases
// report tries, and running each combination as a full recalculation on a copy of the case.
import { ageAtYearEnd } from '@divorceos/engine';
import type { Case, CaseResult, PartyIndex } from '@divorceos/schema';
import { childNcpPercent } from '../lib/timeshare';

export interface ReleaseChild {
  index: number;
  name: string;
  age: number | null;
  /** Child tax credit (under 17 at year end; no date of birth counts as under 17) or credit for other dependents. */
  credit: 'CTC' | 'ODC';
  custodial: PartyIndex;
  /** Can be released by `releaser` (lives with that parent, not an exactly-equal timeshare). */
  releasable: boolean;
  /** Why not, when not releasable. */
  why: string;
}

/** Every child with its release facts; `releaser` is the lower-net parent (null when unknown). */
export function releaseChildren(c: Case, releaser: PartyIndex | null): ReleaseChild[] {
  return c.children.map((ch, index) => {
    const age = ageAtYearEnd(ch.dateOfBirth, c.taxYear);
    const half = Math.abs(childNcpPercent(ch) - 50) < 1e-9;
    const releasable = releaser !== null && ch.custodialParent === releaser && !half;
    const why = releasable ? '' : releaser === null ? 'no result yet' : half ? 'equal timeshare' : 'lives with the other parent';
    return { index, name: ch.name || `Child ${index + 1}`, age, credit: (age ?? 0) < 17 ? 'CTC' : 'ODC', custodial: ch.custodialParent, releasable, why };
  });
}

/**
 * The combinations the report tries: every subset (including none) for up to 4 releasable children; otherwise none,
 * each child alone, and all of them. Sorted by size, then by index.
 */
export function releaseSubsets(indices: number[]): number[][] {
  const ids = [...new Set(indices)].sort((a, b) => a - b);
  if (ids.length <= 4) {
    const out: number[][] = [];
    for (let mask = 0; mask < 1 << ids.length; mask++) out.push(ids.filter((_, i) => mask & (1 << i)));
    return out.sort((a, b) => a.length - b.length || a.join(',').localeCompare(b.join(','), 'en', { numeric: true }));
  }
  return [[], ...ids.map((i) => [i]), ids];
}

/** A copy of the case that releases exactly `subset` in the Proposed column, with guideline amounts (tactic 9). */
export function withReleasedChildren(c: Case, subset: number[]): Case {
  const x = structuredClone(c);
  x.settings.releaseExemptions8332 = true;
  x.proposed.tactic = '9';
  x.proposed.sub = 0;
  x.proposed.releaseChildren = [...subset];
  x.proposed.releases = subset.length;
  return x;
}

export interface ComboRow {
  children: number[];
  /** Income tax (federal + state) per party, monthly, Proposed column. */
  tax: [number, number];
  delta: [number, number];
  /** Combined tax saving against releasing none (positive = saving). */
  combined: number;
  /** Net spendable income per party, monthly. */
  ns: [number, number];
  /** Releases the engine actually applied. */
  applied: number;
  error?: string;
}

/** Runs every combination through `compute`; the first row (no releases) is the baseline. */
export function runReleaseCombos(c: Case, subsets: number[][], compute: (c: Case) => CaseResult): ComboRow[] {
  const rows: ComboRow[] = [];
  let base: [number, number] | null = null;
  for (const s of subsets) {
    try {
      const r = compute(withReleasedChildren(c, s));
      const P = r.parties;
      const tax: [number, number] = [P[0].proposed.taxes.federalIncome + P[0].proposed.taxes.stateIncome, P[1].proposed.taxes.federalIncome + P[1].proposed.taxes.stateIncome];
      if (!base) base = tax;
      const delta: [number, number] = [tax[0] - base[0], tax[1] - base[1]];
      rows.push({ children: s, tax, delta, combined: 0 - (delta[0] + delta[1]), ns: [P[0].proposed.netSpendable, P[1].proposed.netSpendable], applied: r.proposed.releases });
    } catch (e) {
      rows.push({ children: s, tax: [0, 0], delta: [0, 0], combined: 0, ns: [0, 0], applied: 0, error: e instanceof Error ? e.message : String(e) });
    }
  }
  return rows;
}

/** The row with the largest combined saving (ties: fewer children), or null when nothing saves. */
export function bestCombo(rows: ComboRow[]): ComboRow | null {
  let best: ComboRow | null = null;
  for (const r of rows) if (!r.error && r.combined > 0.005 && (!best || r.combined > best.combined + 0.005)) best = r;
  return best;
}
